import { FC, Fragment, useEffect, useState } from 'react';
import * as yup from 'yup';
import { UploadCloud, File } from 'react-feather';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import styled from 'styled-components';
import { OperationVariables, QueryResult, useQuery } from '@apollo/client';
import {
  Button,
  ErrorComponent,
  FieldRow,
  Flex,
  FormikUploaderOther,
  Modal,
  Spacer,
  StyledForm,
  StyledFormGroup,
  StyledFormikDatePicker,
  StyledFormPhoneFormik,
  StyledFormSelectFormik,
  TitleBar,
  WrappedQuery,
} from '../../../../components/shared';
import {
  ALL_GENDERS,
  ALL_HOME_LANGUAGES,
  ALL_POPULATION_GROUPS,
  ALL_NATIONALITIES,
  CHECK_DUPLICATE_PASSPORT_NUMBER_OR_ID_NUMBER,
  GET_FILE,
} from '../../../../graphql';
import { useRouter } from '../../../../hooks';
import { Span } from '../../../../typography';
import { formatSelectValue, isEmpty, onComplete, stripSelectValues } from '../../../../utils';
import { Option, PeopleProfileFormType } from '../../../../utils/types';
import {
  FieldsForCommunityMember,
  FieldsForDistrictOfficial,
  FieldsForLearner,
  FieldsForOfficials,
  FieldsForProvincialOfficial,
  FieldsForSchoolOfficial,
  FieldsForSocialServiceProfessionals,
  FieldsForStudent,
  FieldsForPeople,
} from '../fields';
import { validateSAIDNumber, upsertPersonLocally } from '../../../../utils';
import FieldsForPractitioner from '../fields/FieldsForPractitioner';
import {
  getAllGenders,
  getAllHomeLanguages,
  getAllNationalities,
  getAllPopulationGroups,
} from '../../../../utils/idbFns';
import { client } from '../../../../apollo';
import FileForm from '../fields/FileForm';

const Actions = styled.div`
  display: flex;
`;

type FORMTYPE = 'CREATE' | 'ADDNEW' | 'EDIT';

type Props = {
  mutation: any;
  error?: any;
  buttonText?: string;
  loading: boolean;
  initialValues: PeopleProfileFormType;
  id: string;
  type: FORMTYPE;
  portal: string;
  userRoles?: any;
  allStakeholderTypes?: any;
  institution?: any;
  userId?: string;
  personTypeString?: string | null;
  householdIdString?: string | null;
};

export type PersonDetailsFormType = PeopleProfileFormType & {
  ministryId: Option | undefined;
  formType: FORMTYPE;
  portal: string;
  nosubmit: boolean;
};

// const validationSchema = yup.object().shape({
//   firstName: yup
//     .string()
//     .label('Name')
//     .required(),
//   lastName: yup
//     .string()
//     .label('Surname')
//     .required(),
// });

const validationSchema = yup.object().shape({
  householdCode: yup
    .string()
    .transform(v => (v ? v.trim().toUpperCase() : v))
    .matches(/^[A-Z]$/, 'Must be a single uppercase letter (A-Z)'),
});

export const getDateOfBirth = (idNumber: string) => {
  const isInvalidId = validateSAIDNumber(idNumber);
  if (!isInvalidId && idNumber) {
    // Determine the century based on the third digit
    const year = parseInt(idNumber.substr(0, 2));
    const month = parseInt(idNumber.substr(2, 2));
    const day = parseInt(idNumber.substr(4, 2));

    // Get the current year (assumption: all users are born after 1930)
    const currentYear = new Date().getFullYear() % 99;

    // Determine the full year based on the assumption that all users are born after 1930
    const fullYear = year >= currentYear ? 1900 + year : 2000 + year;

    // Create a Date object with the extracted date of birth
    const dateOfBirth = new Date(fullYear, month - 1, day);

    return dateOfBirth;
  }

  return null;
};

export const getGenderFromSAID = (idNumber: string) => {
  const isInvalidId = validateSAIDNumber(idNumber);
  if (!isInvalidId && idNumber) {
    const genderDigits = parseInt(idNumber.substr(6, 4));

    const gender =
      genderDigits <= 9999 && genderDigits >= 5000
        ? 'Male'
        : genderDigits <= 4999 && genderDigits >= 0
        ? 'Female'
        : null;

    return gender;
  }

  return null;
};

export const getCitizenshipFromSAID = (idNumber: string) => {
  const isInvalidId = validateSAIDNumber(idNumber);
  if (!isInvalidId && idNumber) {
    const citizenshipDigit = parseInt(idNumber.charAt(10));

    const citizenship = citizenshipDigit === 0 ? 'South African' : null;

    return citizenship;
  }
  return null;
};

export const calculateAge = (dateOfBirth: any) => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

export const determineAgeRange = (age?: number) => {
  if (age) {
    if (age < 1) {
      return { label: '0 - 1', value: '0 - 1' };
    }
    if (1 <= age && 2 > age) {
      return { label: '1 - 2', value: '1 - 2' };
    }
    if (2 <= age && 3 > age) {
      return { label: '2 - 3', value: '2 - 3' };
    }
    if (3 <= age && 4 > age) {
      return { label: '3 - 4', value: '3 - 4' };
    }
    if (4 <= age && 5 > age) {
      return { label: '4 - 5', value: '4 - 5' };
    }
    if (5 <= age && 6 > age) {
      return { label: '5 - 6', value: '5 - 6' };
    }
  }
  return null;
};

const validateSaId = (value: any, date: Date | null, citizenship: any) => {
  let error = validateSAIDNumber(value);

  if (!error) {
    if (date) {
      let dateOfBirth = new Date(date);
      const dobFromId = getDateOfBirth(value);

      if (dobFromId)
        if (
          !(
            dobFromId.getFullYear() === dateOfBirth.getFullYear() &&
            dobFromId.getMonth() === dateOfBirth.getMonth() &&
            dobFromId.getDate() === dateOfBirth.getDate()
          )
        ) {
          error = 'The ID Number format is not consistent with the provided Date of birth';
        }
    }
    const isCitizen = getCitizenshipFromSAID(value);

    if (
      ((isCitizen && citizenship && citizenship.label !== 'South African') ||
        (!isCitizen && citizenship && citizenship.label === 'South African')) &&
      value
    ) {
      error = 'The ID Number format is not consistent with the provided Citizenship status';
    }
  }

  return error;
};

/* These functions are not required for now */

// const validateContactNumber = (value: any) => {
//   let error;

//   if (value && !isPossiblePhoneNumber(value)) {
//     error = 'Phone number must be valid';
//   }
//   return error;
// };

// const validateRequiredContactNumber = (value: any) => {
//   let error;

//   const isNotValidString = validateContactNumber(value);

//   if (isNotValidString) {
//     error = isNotValidString;
//   }

//   if (isEmpty(value) && (!value || value === '')) {
//     error = `Contact Number is required.`;
//   }

//   return error;
// };

const PersonDetailsForm: FC<Props> = props => {
  const { history, match } = useRouter();
  const { userRoles, userId } = props;

  const [allNationalities, setAllNationalities] = useState<any[]>([]);
  const [allGender, setAllGender] = useState<any[]>([]);
  const [populationGroups, setPopulationGroups] = useState<any[]>([]);
  const [homeLanguages, setHomeLanguages] = useState<any[]>([]);

  const [submitClicked, setSubmitClicked] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [openModalIfProfileExisting, setOpenModalIfProfileExisting] = useState(false);
  const [person, setPerson] = useState<any>({});
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const tick: number = new Date().getTime();

  const [showUploadProfileImageModal, setShowUploadProfileImageModal] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [fileId, setFileId] = useState<any>(props.initialValues?.profileImage);

  const [profileImageFile, setProfileImageFile] = useState<{
    id?: string;
    name?: string;
  }>({});

  const [profileImageFileExists, setProfileImageFileExists] = useState<string>('');

  const [initialValues, setInitialValues] = useState<PersonDetailsFormType>({
    ...props.initialValues,
    ministryId: undefined,
    formType: props.type,
    portal: props.portal,
    nosubmit: false,
  });

  const { data: profileImageFileData, error: profileImageFileError, loading: profileImageFileLoading } = useQuery(
    GET_FILE,
    {
      skip: !fileId,
      variables: {
        id: fileId,
      },
    }
  );

  useEffect(() => {
    if (profileImageFileData && profileImageFileData.file && profileImageFileData.file.name) {
      setProfileImageFileExists(profileImageFileData.file.name);
    }
  }, [profileImageFileData]);

  const fetchNationalities = async () => {
    try {
      const {
        data: { nationalities },
      }: any = await client.query({
        query: ALL_NATIONALITIES,
        variables: { input: {} },
      });

      if (nationalities) {
        const result = nationalities.map((a: any) => ({
          label: a.name,
          value: a.id,
        }));
        setAllNationalities(result);
      }
    } catch (error) {
      const result = await getAllNationalities();
      const allNationalities = result.map((a: any) => ({
        label: a.name,
        value: a.id,
      }));
      setAllNationalities(allNationalities);
    }
  };

  const fetchGender = async () => {
    try {
      const {
        data: { genders },
      }: any = await client.query({
        query: ALL_GENDERS,
        variables: { input: {} },
      });

      if (genders) {
        const result = genders.map((a: any) => ({
          label: a.name,
          value: a.id,
        }));
        setAllGender(result);
      }
    } catch (error) {
      const result = await getAllGenders();
      const allGenders = result.map((a: any) => ({
        label: a.name,
        value: a.id,
      }));
      setAllGender(allGenders);
    }
  };

  useEffect(() => {
    fetchNationalities();
    fetchGender();
  }, []);

  const getPersonTypes = (institutionType: string | undefined) => {
    let personTypes = props.allStakeholderTypes;
    let defaultType =
      props.allStakeholderTypes &&
      props.allStakeholderTypes.find((b: any) => b.label && b.label.toLowerCase() === 'learners');

    if (institutionType) {
      if (institutionType === 'ECD Service') {
        personTypes = props.allStakeholderTypes.filter((a: any) =>
          ['learners', 'ecd staff', 'community members'].includes(a.label.toLowerCase())
        );
      }
      if (institutionType === 'School') {
        personTypes = props.allStakeholderTypes.filter((a: any) =>
          ['learners', 'school officials', 'community members'].includes(a.label.toLowerCase())
        );
      }
      if (institutionType === 'University') {
        personTypes = props.allStakeholderTypes.filter((a: any) =>
          ['students', 'community members'].includes(a.label.toLowerCase())
        );
        defaultType =
          props.allStakeholderTypes &&
          props.allStakeholderTypes.find((b: any) => b.label && b.label.toLowerCase() === 'students');
      }
      if (institutionType === 'National Office') {
        personTypes = props.allStakeholderTypes.filter((a: any) =>
          ['national officials', 'social services professionals', 'community members'].includes(a.label.toLowerCase())
        );
        defaultType =
          props.allStakeholderTypes &&
          props.allStakeholderTypes.find((b: any) => b.label && b.label.toLowerCase() === 'national officials');
      }
      if (institutionType === 'Provincial Office') {
        personTypes = props.allStakeholderTypes.filter((a: any) =>
          ['provincial officials', 'regional officials', 'social services professionals', 'community members'].includes(
            a.label.toLowerCase()
          )
        );
        defaultType =
          props.allStakeholderTypes &&
          props.allStakeholderTypes.find((b: any) => b.label && b.label.toLowerCase() === 'provincial officials');
      }
      if (institutionType === 'Regional Office') {
        personTypes = props.allStakeholderTypes.filter((a: any) =>
          ['regional officials', 'social services professionals', 'community members'].includes(a.label.toLowerCase())
        );
        defaultType =
          props.allStakeholderTypes &&
          props.allStakeholderTypes.find((b: any) => b.label && b.label.toLowerCase() === 'regional officials');
      }
      if (institutionType === 'District Office') {
        personTypes = props.allStakeholderTypes.filter((a: any) =>
          [
            'district officials',
            'circuit officials',
            'cluster officials',
            'social services professionals',
            'community members',
          ].includes(a.label.toLowerCase())
        );
        defaultType =
          props.allStakeholderTypes &&
          props.allStakeholderTypes.find((b: any) => b.label && b.label.toLowerCase() === 'district officials');
      }
      if (institutionType === 'Circuit Office') {
        personTypes = props.allStakeholderTypes.filter((a: any) =>
          ['circuit officials', 'social services professionals', 'community members'].includes(a.label.toLowerCase())
        );
        defaultType =
          props.allStakeholderTypes &&
          props.allStakeholderTypes.find((b: any) => b.label && b.label.toLowerCase() === 'circuit officials');
      }
      if (institutionType === 'Cluster Office') {
        personTypes = props.allStakeholderTypes.filter((a: any) =>
          ['cluster officials', 'social services professionals', 'community members'].includes(a.label.toLowerCase())
        );
        defaultType =
          props.allStakeholderTypes &&
          props.allStakeholderTypes.find((b: any) => b.label && b.label.toLowerCase() === 'cluster officials');
      }
    }

    return { personTypes, defaultType };
  };

  const getCommunityMembersType = () => {
    return props.allStakeholderTypes.find((a: any) => a.label && a.label.toLowerCase() === 'community members');
  };

  useEffect(() => {
    // Update the initial values when props.initialValues changes
    setInitialValues({
      ...props.initialValues,
      ministryId: undefined,
      formType: props.type,
      portal: props.portal,
      nosubmit: false,
      ...(!props.id &&
        props.institution && {
          typeId: getPersonTypes(props.institution && props.institution.type && props.institution.type.name)
            .defaultType,
        }),
      ...(!props.initialValues.age &&
        props.initialValues.dateOfBirth && {
          age: calculateAge(props.initialValues.dateOfBirth),
        }),
      ...(!props.id &&
        props.personTypeString === 'communityMember' && {
          typeId: getCommunityMembersType(),
        }),
      ...(!props.id &&
        props.householdIdString && {
          householdId: props.householdIdString,
        }),
    });
  }, [
    props.institution,
    props.allStakeholderTypes,
    props.initialValues,
    props.personTypeString,
    props.householdIdString,
  ]);

  const validateForm = (formikProps: any) => {
    return true;

    /* The requirement to check if a learner has at least on caregiver is being parked as we need to 
       confirm if users have caregiver information 
    */

    // eslint-disable-next-line no-unreachable
    if (
      formikProps.values &&
      formikProps.values.typeId &&
      formikProps.values.typeId.label === 'Learners' &&
      !(formikProps.values.caregiverList && formikProps.values.caregiverList.length)
    ) {
      setSubmissionError('A Learner should have at least one caregiver');

      return false;
    } else {
      setSubmissionError('');

      return true;
    }
  };

  const onVerify = async (passportNumber: string, IDNumber: string, privateToOrganisation: boolean) => {
    if (passportNumber || IDNumber) {
      setVerificationLoading(true);

      try {
        const result = await client.query({
          query: CHECK_DUPLICATE_PASSPORT_NUMBER_OR_ID_NUMBER,
          variables: {
            input: {
              currentPersonId: props.id,
              passportNumber,
              IDNumber,
              privateToOrganisation,
            },
          },
        });

        if (result && result.data && result.data.person) {
          const { person } = result.data;
          setPerson(person);
          setOpenModalIfProfileExisting(!openModalIfProfileExisting);
          setVerificationLoading(false);
          return true;
        }

        setVerificationLoading(false);
      } catch (e) {
        const error: any = e;
        console.error(error && error.message ? error.message : error);
        setVerificationLoading(false);
      }
    }

    return false;
  };

  const getOrganisationName = (personObject: any) => {
    if (personObject && personObject.associatedInstitutions && personObject.associatedInstitutions.length > 0) {
      if (personObject.associatedInstitutions[0].institution) {
        return personObject.associatedInstitutions[0].institution.name;
      }
    }

    if (personObject && personObject.associatedOrganisations && personObject.associatedOrganisations.length > 0) {
      if (personObject.associatedOrganisations[0].organisation) {
        if (personObject.associatedOrganisations[0].organisation.institution) {
          return personObject.associatedOrganisations[0].organisation.institution.name;
        }
        if (personObject.associatedOrganisations[0].organisation.funder) {
          return personObject.associatedOrganisations[0].organisation.funder.name;
        }
        if (personObject.associatedOrganisations[0].organisation.serviceProvider) {
          return personObject.associatedOrganisations[0].organisation.serviceProvider.name;
        }
      }
    }

    if (personObject && personObject.institutions && personObject.institutions.length > 0) {
      if (personObject.institutions[0]) {
        return personObject.institutions[0].name;
      }
    }

    return '';
  };

  const scrollToErrorField = (key: any) => {
    if (!submitClicked) return;

    setOpenErrorModal(true);
    const elementName = key;

    let htmlElement =
      elementName && document.getElementsByName(elementName)[0] && document.getElementsByName(elementName)[0];

    if (!htmlElement && elementName) {
      const labels = document.getElementsByTagName('label');
      for (let i = 0; i < labels.length; i++) {
        if (labels[i].htmlFor === elementName) {
          htmlElement = labels[i];
          break;
        }
      }
    }

    if (htmlElement) {
      htmlElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      htmlElement.focus({ preventScroll: true });
    }
    setSubmitClicked(false);
  };

  const validate = (value: any, label: any, key: any, props: any) => {
    let error;

    if (isEmpty(value) && (!value || value === '')) {
      error = `${label} is required.`;
      scrollToErrorField(key);
    }

    return error;
  };

  return (
    <Fragment>
      <Modal
        open={openErrorModal}
        onClose={() => setOpenErrorModal(!openErrorModal)}
        body="Some fields contain errors!"
        heading="Errors detected"
        actions={
          <FieldRow justifyContent="flex-end">
            <Flex direction="row" size="1" justify="flex-end">
              <Button
                type="button"
                p={0}
                width="40px"
                text="Close"
                onClick={() => {
                  setOpenErrorModal(!openErrorModal);
                }}
              />
            </Flex>
          </FieldRow>
        }
      />
      <Modal
        open={openModalIfProfileExisting}
        onClose={() => {
          setOpenModalIfProfileExisting(!openModalIfProfileExisting);
        }}
        heading={`${person && person.firstName} already exists and is associated with ${getOrganisationName(person)}`}
        zIndex={1000}
      >
        <FieldRow justifyContent="center">
          <Flex direction="row" size="1" justify="center">
            <Flex direction="row" size="0.5" flexWrap="nowrap">
              <Button
                type="button"
                text="Cancel"
                mr={3}
                ml={3}
                onClick={() => {
                  setOpenModalIfProfileExisting(!openModalIfProfileExisting);
                }}
              />
              <Button
                bg="warning"
                type="button"
                p={0}
                mr={3}
                width="40px"
                text="View"
                onClick={() => {
                  if (props.id) {
                    history.push(match.url.replace(props.id, person.id));
                  } else {
                    history.push(match.url.replace('create', person.id));
                  }
                  setOpenModalIfProfileExisting(!openModalIfProfileExisting);
                }}
              />
              <Button
                bg="error"
                type="button"
                p={0}
                width="40px"
                text="Discard"
                onClick={() => {
                  setOpenModalIfProfileExisting(!openModalIfProfileExisting);
                  if (props.id) {
                    history.push(match.url.replace(`/${props.id}`, ''));
                  } else {
                    history.push(match.url.replace('/create', ''));
                  }
                }}
              />
            </Flex>
          </Flex>
        </FieldRow>
      </Modal>
      <Modal
        open={showUploadProfileImageModal}
        onClose={() => {
          setProfileImageFile({});
          setShowUploadProfileImageModal(!showUploadProfileImageModal);
        }}
        heading={`Manage File: ${profileImageFile.name}`}
      >
        <FileForm
          file={profileImageFile}
          refetchQueryFunction={() => {
            return [
              {
                query: GET_FILE,
                variables: {
                  id: props.initialValues?.profileImage,
                },
              },
            ];
          }}
          onDone={() => {
            setProfileImageFile({});
            setShowUploadProfileImageModal(!showUploadProfileImageModal);
          }}
          onDelete={() => {
            setProfileImageFileExists('');
          }}
        />
      </Modal>
      <Formik
        initialValues={initialValues}
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={async values => {
          if (values.nosubmit) return;

          try {
            setSubmissionError('');
            const copyOfValues = Object.assign({}, values);
            copyOfValues.associatedInstitutions = values.associatedInstitutions.map(a => Object.assign({}, a));

            copyOfValues.associatedOrganisations = values.associatedOrganisations.map(a => Object.assign({}, a));

            const formattedValues: any = stripSelectValues(copyOfValues);
            formattedValues.associatedInstitutions = formattedValues.associatedInstitutions.map((i: any) => {
              delete i.provinceId;
              delete i.districtId;
              delete i.localMunicipalityId;
              delete i.communityTypeId;
              delete i.districtMunicipalityId;

              return {
                ...i,
                id: i.id.startsWith('create') ? '' : i.id,
                institution: i.institution.id,
              };
            });
            formattedValues.associatedOrganisations = formattedValues.associatedOrganisations.map((i: any) => {
              return {
                ...i,
                id: i.id.startsWith('create') ? '' : i.id,
                organisation: i.organisation.id,
              };
            });

            formattedValues.caregiverList = formattedValues.caregiverList.map((i: any) => {
              return {
                ...i,
                id: i.id.startsWith('create') ? '' : i.id,
              };
            });

            delete formattedValues.emisNumber;
            delete formattedValues.ministryId;
            delete formattedValues.formType;
            delete formattedValues.portal;
            delete formattedValues.nosubmit;

            if (formattedValues.caregiver) {
              delete formattedValues.caregiver;
            }

            if (formattedValues.contactNumberCountry) {
              delete formattedValues.contactNumberCountry;
            }
            if (formattedValues.careGiverContactNumberCountry) {
              delete formattedValues.careGiverContactNumberCountry;
            }

            await props.mutation({
              variables: {
                input: {
                  id: props.id,
                  ...formattedValues,
                  institutionId: props.institution && props.institution.id,
                },
              },
            });
          } catch (error) {
            const rawMessage =
              error instanceof Error ? error.message : typeof error === 'string' ? error : (error as any)?.message;
            const errorMessage = rawMessage ? rawMessage.replace('GraphQL error:', '').trim() : 'Submission failed';
            setSubmissionError(errorMessage);
            if (error) {
              upsertPersonLocally(
                {
                  ...JSON.parse(JSON.stringify(values)),
                  id: props.id ? props.id : `create_person${tick}`,
                },
                userId,
                props.institution && props.institution.id
              );
            }

            return error;
          }
        }}
        render={(formikProps: FormikProps<PersonDetailsFormType>) => {
          const { values } = formikProps;

          return (
            <StyledForm flexDirection="column">
              <TitleBar bg="transparent" color="black" pl={0}>
                Person
              </TitleBar>
              {/* <FieldRow justifyContent="flex-end" /> */}
              <Spacer spacing={10} />
              <FieldRow>
                <FieldRow flex={0.8}>
                  <Field
                    name="typeId"
                    render={({ field }: FieldProps<PersonDetailsFormType>) => (
                      <StyledFormSelectFormik
                        {...field}
                        field={field}
                        label="Type"
                        form={formikProps}
                        isDisabled={!!(props.personTypeString && !props.id)}
                        options={
                          getPersonTypes(props.institution && props.institution.type && props.institution.type.name)
                            .personTypes
                        }
                        mr={2}
                      />
                    )}
                  />
                </FieldRow>
                <FieldRow flex={0.2}>
                  <Field
                    name="privateToOrganisation"
                    render={({ field }: FieldProps<PersonDetailsFormType>) => (
                      <StyledFormGroup
                        {...field}
                        field={field}
                        form={formikProps}
                        label="Private To Organisation"
                        type="checkbox"
                        ml={4}
                        defaultChecked={formikProps.values.privateToOrganisation}
                        wrapperWidth="40px"
                        width="40px"
                      />
                    )}
                  />
                </FieldRow>
              </FieldRow>
              <FieldRow>
                <Field
                  name="profileImage"
                  render={({ field }: FieldProps<PersonDetailsFormType>) => (
                    <Fragment>
                      {uploadError ? <ErrorComponent message={uploadError} /> : null}

                      {formikProps.values.profileImage && (
                        <Fragment>
                          {!profileImageFileLoading && !profileImageFileError && profileImageFileExists && (
                            <Fragment>
                              <Button
                                bg="success"
                                icon={<File />}
                                isIcon={true}
                                mr={1}
                                p={0}
                                type="button"
                                text="Download File"
                                width="40px"
                                style={{
                                  fontSize: '10px',
                                  marginTop: '10px',
                                  marginBottom: '10px',
                                }}
                                onClick={() => {
                                  setProfileImageFile(profileImageFileData.file);
                                  setShowUploadProfileImageModal(!showUploadProfileImageModal);
                                }}
                              />
                              <div
                                style={{
                                  marginTop: '10px',
                                  marginBottom: '10px',
                                  marginRight: '35px',
                                }}
                              >
                                {profileImageFileData && profileImageFileData.file && profileImageFileData.file.name}
                              </div>
                            </Fragment>
                          )}
                        </Fragment>
                      )}

                      <Button
                        type="button"
                        id="uppy1-open"
                        text="Profile Image"
                        hidden={!!profileImageFileExists}
                        width="50px"
                        mr={4}
                        icon={<UploadCloud size={10} color="white" />}
                        style={{ fontSize: '10px', marginTop: '10px', marginBottom: '10px' }}
                      />

                      <FormikUploaderOther
                        onComplete={onComplete}
                        setUploadError={setUploadError}
                        uploadError={uploadError}
                        client={client}
                        setFileId={setFileId}
                        {...field}
                        field={field}
                        form={formikProps}
                      />
                    </Fragment>
                  )}
                />
              </FieldRow>
              <FieldRow>
                <Field
                  name="firstName"
                  render={({ field }: FieldProps<PersonDetailsFormType>) => (
                    <StyledFormGroup {...field} field={field} form={formikProps} label="Name *" mr={4} />
                  )}
                  validate={(value: any) => validate(value, 'Name', 'firstName', formikProps)}
                />
                <Field
                  name="lastName"
                  render={({ field }: FieldProps<PersonDetailsFormType>) => (
                    <StyledFormGroup {...field} field={field} form={formikProps} label="Surname *" mr={4} />
                  )}
                  validate={(value: any) => validate(value, 'Surname', 'lastName', formikProps)}
                />

                <Field
                  name="genderId"
                  render={({ field }: FieldProps<PersonDetailsFormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Gender"
                      form={formikProps}
                      mr={4}
                      options={allGender}
                    />
                  )}
                  // {...(values.typeId &&
                  //   values.typeId.label === 'Learners' && {
                  //     validate: (value: any) => validate(value, 'Gender', 'genderId', formikProps),
                  //   })}
                />

                <Field
                  name="dateOfBirth"
                  render={() => (
                    <StyledFormikDatePicker
                      isSingleDate={true}
                      form={formikProps}
                      label="Date of Birth"
                      dateLabel="dateOfBirth"
                      date={values.dateOfBirth}
                      format={'d-M-yyyy'}
                      changeHandler={(date: any) => {
                        values.dateOfBirth = date;
                        const age = calculateAge(date);
                        formikProps.setFieldValue('age', age);
                        const ageRange = determineAgeRange(age);
                        if (ageRange) {
                          formikProps.setFieldValue('ageRange', ageRange);
                        }
                      }}
                    />
                  )}
                  // {...(values.typeId &&
                  //   values.typeId.label === 'Learners' && {
                  //     validate: (value: any) => validate(value, 'Date of Birth', 'dateOfBirth', formikProps),
                  //   })}
                />
              </FieldRow>

              <FieldRow>
                {values.typeId && (values.typeId.label === 'Learners' || values.typeId.label === 'Community Members') && (
                  <Field
                    name="citizenshipId"
                    render={({ field }: FieldProps<PersonDetailsFormType>) => (
                      <StyledFormSelectFormik
                        {...field}
                        field={field}
                        label="Citizenship"
                        form={formikProps}
                        options={allNationalities}
                        changeHandler={(option: any) => {
                          if (option.label.toLowerCase() === 'south african') {
                            formikProps.setFieldValue('identificationType', {
                              label: 'RSA ID',
                              value: 'RSA ID',
                            });
                          }
                        }}
                        mr={4}
                      />
                    )}
                    // {...values.typeId &&
                    //   values.typeId.label === 'Learners' && {
                    //     validate: (value: any) => validate(value, 'Citizenship'),
                    //   }}
                  />
                )}
                {values.typeId && (values.typeId.label === 'Learners' || values.typeId.label === 'Community Members') && (
                  <Field
                    name="identificationType"
                    render={({ field }: FieldProps<PersonDetailsFormType>) => (
                      <StyledFormSelectFormik
                        {...field}
                        field={field}
                        label="Identification Type"
                        form={formikProps}
                        options={[
                          { label: 'RSA ID', value: 'RSA ID' },
                          {
                            label: 'Passport',
                            value: 'Passport',
                          },
                        ]}
                      />
                    )}
                    // {...(values.typeId &&
                    //   values.typeId.label === 'Learners' && {
                    //     validate: (value: any) =>
                    //       validate(value, 'Identification Type', 'identificationType', formikProps),
                    //   })}
                  />
                )}
                {values.typeId &&
                  (values.typeId.label === 'Learners' || values.typeId.label === 'Community Members') &&
                  values.identificationType &&
                  values.identificationType.label === 'RSA ID' && (
                    <Field
                      name="birthCertificateOrId"
                      render={({ field }: FieldProps<PersonDetailsFormType>) => (
                        <StyledFormGroup
                          {...field}
                          field={field}
                          form={formikProps}
                          label="Birth Certificate/ID"
                          type="checkbox"
                          ml={4}
                          defaultChecked={formikProps.values.birthCertificateOrId}
                          wrapperWidth="40px"
                          width="40px"
                        />
                      )}
                    />
                  )}
              </FieldRow>
              <FieldRow>
                {values.typeId &&
                  (values.typeId.label === 'Learners' || values.typeId.label === 'Community Members') &&
                  !values.birthCertificateOrId &&
                  values.identificationType &&
                  values.identificationType.label === 'RSA ID' && (
                    <>
                      <Field
                        name="appliedForBirthCertificateOrId"
                        render={({ field }: FieldProps<PersonDetailsFormType>) => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Applied for Birth Certificate/ID"
                            type="checkbox"
                            mr={3}
                            defaultChecked={formikProps.values.appliedForBirthCertificateOrId}
                            wrapperWidth="40px"
                            width="40px"
                          />
                        )}
                      />

                      <Flex direction="row" size="2">
                        <Field
                          name="reasonForLackOfBirthCertificateOrId"
                          render={({ field }: FieldProps<PersonDetailsFormType>) => (
                            <StyledFormGroup
                              {...field}
                              field={field}
                              form={formikProps}
                              label="Reason for Lack of Birth Certificate/ID"
                              mr={4}
                            />
                          )}
                        />
                      </Flex>
                    </>
                  )}
                {values.typeId &&
                  (values.typeId.label === 'Learners' || values.typeId.label === 'Community Members') &&
                  formikProps.values.birthCertificateOrId &&
                  values.identificationType &&
                  values.identificationType.label === 'RSA ID' && (
                    <Flex direction="row" size="1">
                      <Field
                        name="nationalID"
                        render={({ field }: FieldProps<PersonDetailsFormType>) => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="National ID"
                            onChange={(event: any) => {
                              formikProps.setFieldValue('nationalID', event.target.value);
                              const dob = getDateOfBirth(event.target.value);
                              const gender = getGenderFromSAID(event.target.value);
                              const citizenship = getCitizenshipFromSAID(event.target.value);

                              if (dob && !formikProps.values.dateOfBirth) {
                                formikProps.setFieldValue('dateOfBirth', dob);
                                const age = calculateAge(dob);
                                if (age) {
                                  formikProps.setFieldValue('age', age);
                                  const ageRange = determineAgeRange(age);
                                  if (ageRange) {
                                    formikProps.setFieldValue('ageRange', ageRange);
                                  }
                                }
                              }

                              if (gender) {
                                const genderValue = allGender.find(
                                  (a: any) => a.label && a.label.toLowerCase() === gender.toLowerCase()
                                );

                                formikProps.setFieldValue('genderId', genderValue);
                              }
                              if (citizenship && !formikProps.values.citizenshipId) {
                                const citizenshipValue = allNationalities.find(
                                  (a: any) => a.label && a.label.toLowerCase() === citizenship.toLowerCase()
                                );

                                if (citizenshipValue) {
                                  formikProps.setFieldValue('citizenshipId', citizenshipValue);
                                }
                              }
                            }}
                            {...(values.typeId &&
                              values.typeId.label === 'Learners' && {
                                mr: 4,
                              })}
                          />
                        )}
                        validate={(value: any) =>
                          validateSaId(value, formikProps.values.dateOfBirth, formikProps.values.citizenshipId)
                        }
                      />
                    </Flex>
                  )}
                {values.typeId &&
                  (values.typeId.label === 'Learners' || values.typeId.label === 'Community Members') &&
                  values.identificationType &&
                  values.identificationType.label === 'Passport' && (
                    <Field
                      name="passport"
                      render={({ field }: FieldProps<PersonDetailsFormType>) => (
                        <StyledFormGroup
                          {...field}
                          field={field}
                          form={formikProps}
                          label="Passport Number"
                          mr={4}
                          onChange={(event: any) => {
                            formikProps.setFieldValue('passport', event.target.value);
                          }}
                        />
                      )}
                    />
                  )}
                {values.typeId &&
                  (values.typeId.label === 'Learners' || values.typeId.label === 'Community Members') &&
                  values.identificationType &&
                  ((values.identificationType.label === 'RSA ID' && formikProps.values.birthCertificateOrId) ||
                    values.identificationType.label === 'Passport') && (
                    <FieldRow justifyContent="flex-end" flex={0.2}>
                      <Button
                        bg="warning"
                        type="button"
                        text="verify"
                        width="40px"
                        mt={4}
                        mr={4}
                        disabled={!(formikProps.values.passport || formikProps.values.nationalID)}
                        isLoading={verificationLoading}
                        onClick={() => {
                          onVerify(
                            formikProps.values.passport,
                            formikProps.values.nationalID,
                            formikProps.values.privateToOrganisation
                          );
                        }}
                      />
                    </FieldRow>
                  )}
                {values.typeId &&
                  values.typeId.label === 'Learners' &&
                  values.citizenshipId &&
                  values.citizenshipId.label &&
                  values.citizenshipId.label.toLowerCase() !== 'south african' &&
                  ((values.age && values.age > 4) || (values.dateOfBirth && calculateAge(values.dateOfBirth) > 4)) && (
                    <Field
                      name="studyPermit"
                      render={({ field }: FieldProps<PersonDetailsFormType>) => (
                        <StyledFormSelectFormik
                          {...field}
                          field={field}
                          label="Study Permit"
                          form={formikProps}
                          options={[
                            { label: 'Yes', value: 'Yes' },
                            { label: 'No', value: 'No' },
                          ]}
                          mr={4}
                        />
                      )}
                    />
                  )}
                {values.typeId &&
                  values.typeId.label === 'Learners' &&
                  values.studyPermit &&
                  values.studyPermit.label === 'Yes' && (
                    <Field
                      name="studyPermitNumber"
                      render={({ field }: FieldProps<PersonDetailsFormType>) => (
                        <StyledFormGroup
                          {...field}
                          field={field}
                          form={formikProps}
                          label="Study Permit Number"
                          mr={4}
                        />
                      )}
                    />
                  )}
                {values.typeId && values.typeId.label === 'Learners' && values.dateOfBirth && (
                  <Field
                    name="age"
                    render={({ field }: FieldProps<PersonDetailsFormType>) => (
                      <StyledFormGroup
                        {...field}
                        field={field}
                        form={formikProps}
                        label="Age"
                        type="number"
                        disabled={true}
                        readOnly={true}
                        value={calculateAge(values.dateOfBirth)}
                        onChange={() => {}}
                      />
                    )}
                  />
                )}

                {values.typeId &&
                  values.typeId.label === 'Learners' &&
                  ((values.age && values.age < 6) || (values.dateOfBirth && calculateAge(values.dateOfBirth) < 6)) && (
                    <Field
                      name="ageRange"
                      render={({ field }: FieldProps<PersonDetailsFormType>) => (
                        <StyledFormSelectFormik
                          {...field}
                          field={field}
                          label="Age Range (Year)"
                          form={formikProps}
                          readOnly={true}
                          options={[
                            { label: '0 - 1', value: '0 - 1' },
                            {
                              label: '1 - 2',
                              value: '1 - 2',
                            },
                            {
                              label: '2 - 3',
                              value: '2 - 3',
                            },
                            {
                              label: '3 - 4',
                              value: '3 - 4',
                            },
                            {
                              label: '4 - 5',
                              value: '4 - 5',
                            },
                            {
                              label: '5 - 6',
                              value: '5 - 6',
                            },
                          ]}
                          ml={4}
                        />
                      )}
                    />
                  )}
              </FieldRow>
              <FieldRow>
                <WrappedQuery query={ALL_POPULATION_GROUPS} overrideStates={true}>
                  {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                    if (data && data.populationGroups) {
                      setPopulationGroups(data.populationGroups);
                    }

                    if (error) {
                      getAllPopulationGroups().then((data: any[]) => setPopulationGroups(data));
                    }

                    return (
                      <Field
                        name="populationGroupId"
                        render={({ field }: FieldProps<PersonDetailsFormType>) => (
                          <StyledFormSelectFormik
                            {...field}
                            field={field}
                            label={`Population Group`}
                            form={formikProps}
                            options={
                              loading
                                ? []
                                : populationGroups.map((item: any) =>
                                    formatSelectValue({
                                      label: item.name,
                                      value: item.id,
                                    })
                                  )
                            }
                            mr={4}
                          />
                        )}
                        // {...values.typeId &&
                        //   values.typeId.label === 'Learners' && {
                        //     validate: (value: any) => validate(value, 'Population Group'),
                        //   }}
                      />
                    );
                  }}
                </WrappedQuery>
                <WrappedQuery query={ALL_HOME_LANGUAGES} overrideStates={true}>
                  {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                    if (data && data.homeLanguages) {
                      setHomeLanguages(data.homeLanguages);
                    }

                    if (error) {
                      getAllHomeLanguages().then((data: any) => setHomeLanguages(data));
                    }

                    return (
                      <Field
                        name="homeLanguageId"
                        render={({ field }: FieldProps<PersonDetailsFormType>) => (
                          <StyledFormSelectFormik
                            {...field}
                            field={field}
                            label={`Home Language`}
                            form={formikProps}
                            options={
                              loading
                                ? []
                                : homeLanguages.map((item: any) =>
                                    formatSelectValue({
                                      label: item.name,
                                      value: item.id,
                                    })
                                  )
                            }
                            {...(values.typeId &&
                              values.typeId.label !== 'Learners' && {
                                mr: 4,
                              })}
                          />
                        )}
                        // {...values.typeId &&
                        //   values.typeId.label === 'Learners' && {
                        //     validate: (value: any) => validate(value, 'Home Language'),
                        //   }}
                      />
                    );
                  }}
                </WrappedQuery>
                {((values.typeId && values.typeId.label === 'Learners' && values.age && values.age > 10) ||
                  (values.typeId && values.typeId.label !== 'Learners')) && (
                  <Field
                    name="contactNumber"
                    render={({ field }: FieldProps<PersonDetailsFormType>) => (
                      <StyledFormPhoneFormik
                        {...field}
                        field={field}
                        form={formikProps}
                        defaultCountry="ZA"
                        label="Contact Number"
                        ml={4}
                      />
                    )}
                  />
                )}
                {values.typeId && values.typeId.label === 'Learners' && values.age && values.age > 10 && (
                  <>
                    <Field
                      name="emailAddress"
                      render={({ field }: FieldProps<PersonDetailsFormType>) => (
                        <StyledFormGroup
                          {...field}
                          field={field}
                          form={formikProps}
                          label="Email Address"
                          // mr={4}
                          ml={4}
                        />
                      )}
                    />
                  </>
                )}
              </FieldRow>
              {values.typeId && (
                <Fragment>
                  {values.typeId.label === 'Circuit Officials' && (
                    <FieldsForOfficials formikProps={formikProps} userRoles={userRoles} />
                  )}
                  {values.typeId.label === 'Cluster Officials' && (
                    <FieldsForOfficials formikProps={formikProps} userRoles={userRoles} />
                  )}
                  {values.typeId.label === 'Community Members' && (
                    <FieldsForCommunityMember
                      formikProps={formikProps}
                      submissionError={submissionError}
                      userRoles={userRoles}
                      householdIdString={props.householdIdString}
                    />
                  )}
                  {values.typeId.label === 'Public' && (
                    <FieldsForPeople formikProps={formikProps} userRoles={userRoles} />
                  )}
                  {values.typeId.label === 'District Officials' && (
                    <FieldsForDistrictOfficial formikProps={formikProps} />
                  )}
                  {values.typeId.label === 'Learners' && (
                    <FieldsForLearner
                      formikProps={formikProps}
                      userRoles={userRoles}
                      institution={props.institution}
                      allNationalities={allNationalities}
                      allGender={allGender}
                    />
                  )}
                  {values.typeId.label === 'National Officials' && (
                    <FieldsForProvincialOfficial formikProps={formikProps} userRoles={userRoles} />
                  )}
                  {values.typeId.label === 'Provincial Officials' && (
                    <FieldsForProvincialOfficial formikProps={formikProps} userRoles={userRoles} />
                  )}
                  {values.typeId.label === 'Regional Officials' && (
                    <FieldsForOfficials formikProps={formikProps} userRoles={userRoles} />
                  )}
                  {values.typeId.label === 'School Officials' && (
                    <FieldsForSchoolOfficial formikProps={formikProps} userRoles={userRoles} />
                  )}
                  {values.typeId.label === 'Social Services Professionals' && (
                    <FieldsForSocialServiceProfessionals formikProps={formikProps} userRoles={userRoles} />
                  )}
                  {values.typeId.label === 'Students' && (
                    <FieldsForStudent formikProps={formikProps} userRoles={userRoles} />
                  )}
                  {values.typeId.label === 'Practitioners' && (
                    <FieldsForPractitioner formikProps={formikProps} userRoles={userRoles} />
                  )}
                </Fragment>
              )}
              {props.error && (
                <Fragment>
                  <Spacer spacing={20} />
                  <Span width="100%" textAlign="center" color="error" mb={3}>
                    {props.error.message.replace('GraphQL error:', '').trim()}
                  </Span>
                </Fragment>
              )}
              <Spacer spacing={20} />
              <FieldRow justifyContent="flex-end">
                {((!isEmpty(formikProps.errors) &&
                  submitClicked &&
                  !isEmpty(formikProps.touched) &&
                  !Object.keys(formikProps.touched).find((a: any) => a === 'caregiver')) ||
                  (submitClicked && submissionError)) && (
                  <Flex direction="row" size="1" justify="flex-start" alignItems="center">
                    <div
                      style={{
                        marginTop: '10px',
                        color: '#cc0000',
                        textAlign: 'right',
                        fontWeight: 200,
                        fontSize: ' 20px',
                        textTransform: 'none',
                      }}
                    >
                      {submissionError ? submissionError : 'Scroll up to see the error message(s)'}
                    </div>
                  </Flex>
                )}
                <Flex direction="row" size="1" justify="flex-end">
                  <Actions>
                    <Button
                      type="button"
                      text="Cancel"
                      mr={3}
                      onClick={() => {
                        history.goBack();
                      }}
                    />
                    <Button
                      type="button"
                      text={props.buttonText || 'SUBMIT'}
                      isLoading={props.loading}
                      disabled={props.loading}
                      onClick={async () => {
                        setSubmitClicked(true);
                        if (validateForm(formikProps)) {
                          const result = await onVerify(
                            formikProps.values.passport,
                            formikProps.values.nationalID,
                            formikProps.values.privateToOrganisation
                          );
                          if (!result) {
                            formikProps.submitForm();
                          }
                        }
                      }}
                    />
                  </Actions>
                </Flex>
              </FieldRow>
            </StyledForm>
          );
        }}
      />
    </Fragment>
  );
};

export default PersonDetailsForm;
